import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { motion } from 'motion/react';
import { type FC, useState } from 'react';
import { useLocation } from 'react-router';
import { AnimatedArrow } from '../AnimatedArrow';
import { type Sections, Sidebar, type Props as SidebarProps, type Tabs } from '../Sidebar';

interface Props extends SidebarProps {
  tabs: Tabs;
  sections: Sections;
}

export const ButtonRouter: FC<Props> = ({ tabs, sections }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();

  const currentTab = tabs.find((t) => location.pathname.startsWith(t.path));

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isOpen) return;
    e.stopPropagation();
    setIsOpen(false);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(!isOpen)}>
        <AnimatedArrow isDown={isOpen} showClosedToLeft />
        {currentTab?.icon}
      </Button>
      <>
        <Overlay
          initial={false}
          animate={{ opacity: isOpen ? 0.5 : 0 }}
          style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
          onClick={(e) => handleOverlayClick(e)}
          onTouchStart={(e) => handleOverlayClick(e)}
        />
        <SidebarContainer
          initial={false}
          animate={{
            height: isOpen ? 250 : 0,
            borderColor: isOpen ? RacemapColors.PaleBlue : '#e7e7e7',
            borderWidth: isOpen ? 2 : 0,
          }}
          onAnimationComplete={() => setCollapsed(!isOpen)}
          style={{
            pointerEvents: isOpen ? 'auto' : 'none',
            // `pointer-events` alone leaves the nav links keyboard-reachable. Hiding waits
            // for the collapse to finish so the content does not vanish mid-animation.
            visibility: !isOpen && collapsed ? 'hidden' : 'visible',
          }}
        >
          <Sidebar tabs={tabs} sections={sections} onTabChoosing={() => setIsOpen(false)} />
        </SidebarContainer>
      </>
    </>
  );
};

const Button = styled.button`
  width: 100%;
  height: 100%;
  background: white;
  border: unset;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  font-size: larger;

  :hover {
    background: ${RacemapColors.CloudBlue};
  }
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  left: 0;
  background: white;
  right: 0;
  height: 0;
  overflow: auto;
  border-top: solid 2px ${RacemapColors.PaleBlue};
  border-bottom: solid 2px ${RacemapColors.PaleBlue};
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 106px;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  opacity: 0;
`;
